import React, {
	type MouseEvent,
	type KeyboardEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	createRef,
	Fragment,
} from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import { Label } from '@atlaskit/form';
import { IconTile } from '@atlaskit/icon';
import CloseIcon from '@atlaskit/icon/core/close';
import LinkIcon from '@atlaskit/icon/core/link';
import WarningIcon from '@atlaskit/icon/core/warning';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Grid, Stack, xcss, Flex, Text } from '@atlaskit/primitives';
import Select from '@atlaskit/select';
import type { AtlaskitSelectRefType } from '@atlaskit/select/src/types';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	INWARD_LINK_DIRECTION,
	OUTWARD_LINK_DIRECTION,
} from '@atlassian/jira-issue-shared-types/src/common/types/linked-issue-type.tsx';
import type {
	LinkType,
	Props,
	EventType,
	Option,
} from '../../common/types/edit-dependency-modal.tsx';
import IssueCard from './issue-card/index.tsx';
import IssueInfo from './issue-card/issue-info/index.tsx';
import messages from './messages.tsx';
import UnlinkDependencyButton from './unlink-dependency-button/index.tsx';

const EditDependencyModal = ({
	isOpen,
	issueType,
	issueKey,
	issueSummary,
	iconUrl,
	dependencies,
	options,
	isLoading,
	errors,
	shouldReturnFocus,
	onClose,
	setCloseModal,
	onAddDependency,
	onUnlinkDependency,
}: Props) => (
	<ModalTransition>
		{isOpen ? (
			<EditDependencyModalContent
				issueType={issueType}
				issueKey={issueKey}
				issueSummary={issueSummary}
				iconUrl={iconUrl}
				dependencies={dependencies}
				options={options}
				isLoading={isLoading}
				errors={errors}
				shouldReturnFocus={shouldReturnFocus}
				onClose={onClose}
				setCloseModal={setCloseModal}
				onAddDependency={onAddDependency}
				onUnlinkDependency={onUnlinkDependency}
			/>
		) : null}
	</ModalTransition>
);

const EditDependencyModalContent = ({
	issueType,
	issueKey,
	issueSummary,
	iconUrl,
	dependencies,
	options,
	errors,
	isLoading,
	shouldReturnFocus,
	onClose,
	setCloseModal,
	onAddDependency,
	onUnlinkDependency,
}: Omit<Props, 'isOpen'>) => {
	const { formatMessage } = useIntl();

	const [linkType, setLinkType] = useState<{ label: string; value: LinkType } | null>(null);
	const [linkIssue, setLinkIssue] = useState<Option | null>(null);

	const [isAddButtonLoading, setIsAddButtonLoading] = useState<boolean>(false);

	const linkTypeSelect = useRef<AtlaskitSelectRefType>(null);

	const unlinkButtonRefs = useMemo(
		() => dependencies.map(() => createRef<HTMLButtonElement>()),
		[dependencies],
	);

	const focusNextUnlinkButton = useCallback(
		(index: number) => {
			if (unlinkButtonRefs.length - 1 !== index) {
				for (let i = index + 1; i < unlinkButtonRefs.length; i++) {
					if (!unlinkButtonRefs?.[i].current?.disabled) {
						return unlinkButtonRefs[i].current?.focus();
					}
				}
			}

			linkTypeSelect.current?.focus();
		},
		[unlinkButtonRefs],
	);

	const linkTypeOptions = useMemo(
		() => [
			{ value: INWARD_LINK_DIRECTION, label: formatMessage(messages.blockedBy) },
			{ value: OUTWARD_LINK_DIRECTION, label: formatMessage(messages.blocks) },
		],
		[formatMessage],
	);

	const addDependencyOptions = useMemo(
		() =>
			options.filter(
				(option) =>
					option.issueKey !== issueKey &&
					!dependencies.find((dependency) => dependency.issueKey === option.issueKey),
			),
		[options, issueKey, dependencies],
	);

	useEffect(() => {
		setLinkType(null);
		setLinkIssue(null);
	}, [dependencies]);

	const handleOnClose = useCallback(
		(event: EventType, analyticsEvent: UIAnalyticsEvent) => {
			setCloseModal();
			onClose?.({ event, analyticsEvent });
		},
		[onClose, setCloseModal],
	);

	const handleLinkTypeChange = useCallback((value: { label: string; value: LinkType } | null) => {
		setLinkType(value);
	}, []);

	const handleLinkIssueChange = useCallback((value: Option | null) => {
		setLinkIssue(value);
	}, []);

	const handleAddDependency = useCallback(
		async (event: EventType, analyticsEvent: UIAnalyticsEvent) => {
			if (linkIssue && linkType) {
				linkTypeSelect.current?.focus();
				const newDependency = { ...linkIssue, link: linkType.value };

				setIsAddButtonLoading(true);

				const success = await onAddDependency({
					dependency: newDependency,
					event,
					analyticsEvent,
				});

				setIsAddButtonLoading(false);

				if (success) {
					setLinkIssue(null);
					setLinkType(null);
				}
			}
		},
		[linkIssue, linkType, onAddDependency],
	);

	const getLinkIssueLabel = useCallback(
		(option: Option) => (
			<IssueInfo
				issueType={option.issueType}
				issueKey={option.issueKey}
				issueSummary={option.issueSummary}
				iconUrl={option.iconUrl}
			/>
		),
		[],
	);

	const onStopPropagation = useCallback((event: MouseEvent | KeyboardEvent) => {
		event.stopPropagation();
	}, []);

	const isLoadingLabelMessage = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
		? messages.isLoadingLabelIssueTermRefresh
		: messages.isLoadingLabel;

	const finishedLoadingLabelMessage = expVal(
		'issue-terminology-refresh-m2-replace',
		'isEnabled',
		false,
	)
		? messages.finishedLoadingLabelIssueTermRefresh
		: messages.finishedLoadingLabel;

	return (
		<>
			<Modal
				onClose={handleOnClose}
				label={formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.modalLabelIssueTermRefresh
						: messages.modalLabel,
					{
						issueType,
						issueKey,
						issueSummary,
						isLoading: isLoading
							? formatMessage(
									expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
										? messages.isLoadingLabelIssueTermRefresh
										: messages.isLoadingLabel,
								)
							: '',
					},
				)}
				shouldReturnFocus={shouldReturnFocus}
			>
				<ModalHeader>
					<ModalTitle>{formatMessage(messages.title)}</ModalTitle>
					<IconButton
						appearance="subtle"
						label={formatMessage(messages.closeIconLabel)}
						spacing="compact"
						icon={CloseIcon}
						onClick={handleOnClose}
						interactionName="timeline-edit-dependency-close-clicked"
					/>
				</ModalHeader>
				<ModalBody>
					<ScreenReaderText aria-live="polite">
						{errors.length === 0 ? formatMessage(messages.fixedWarnings) : ''}
						{errors.map((error) => formatMessage(messages.errorMessage, { error }))}
					</ScreenReaderText>

					<ScreenReaderText aria-live="polite">
						{formatMessage(isLoading ? isLoadingLabelMessage : finishedLoadingLabelMessage)}
					</ScreenReaderText>

					<Stack space="space.300">
						{errors.length !== 0 ? (
							<Stack space="space.150">
								{errors.map((error) => (
									<Box xcss={warningStyles} key={error}>
										<WarningIcon
											color={token('color.icon.warning')}
											label={formatMessage(messages.warning)}
										/>
										<Text weight="medium">{error}</Text>
									</Box>
								))}
							</Stack>
						) : null}

						<Box as="span">
							{formatMessage(
								expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
									? messages.descriptionIssueTermRefresh
									: messages.description,
							)}
						</Box>
						<Box>
							<Label htmlFor="">
								{formatMessage(
									expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
										? messages.selectedIssueLabelIssueTermRefresh
										: messages.selectedIssueLabel,
								)}
							</Label>
							{issueSummary.length > 16 ? (
								<Tooltip content={issueSummary}>
									{(tooltipProps) => (
										<IssueCard
											tooltipProps={tooltipProps}
											issueType={issueType}
											issueKey={issueKey}
											issueSummary={issueSummary}
											iconUrl={iconUrl}
										/>
									)}
								</Tooltip>
							) : (
								<IssueCard
									issueType={issueType}
									issueKey={issueKey}
									issueSummary={issueSummary}
									iconUrl={iconUrl}
								/>
							)}
						</Box>

						{!isLoading ? (
							<>
								{dependencies.length !== 0 ? (
									<Box>
										<Label htmlFor="">
											{formatMessage(
												expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
													? messages.linkedIssuesLabelIssueTermRefresh
													: messages.linkedIssuesLabel,
											)}
										</Label>
										<Grid
											templateColumns="auto minmax(0, 1fr) auto"
											alignItems="center"
											rowGap="space.100"
											columnGap="space.150"
											xcss={linkedIssuesContainer}
											testId="roadmap.timeline-table-kit.ui.edit-dependency-modal.linked-issues"
										>
											{dependencies.map((dependency, index) => (
												<Fragment key={dependency.link + dependency.issueKey}>
													<Flex alignItems="center" gap="space.075">
														<IconTile
															icon={LinkIcon}
															appearance="grayBold"
															shape="circle"
															size="24"
															label=""
														/>
														<Box xcss={noWrapStyles}>
															{formatMessage(
																dependency.link === OUTWARD_LINK_DIRECTION
																	? messages.blocks
																	: messages.blockedBy,
															)}
														</Box>
													</Flex>
													<Tooltip content={dependency.issueSummary}>
														{(tooltipProps) => (
															<IssueCard
																tooltipProps={tooltipProps}
																issueType={dependency.issueType}
																issueKey={dependency.issueKey}
																issueSummary={dependency.issueSummary}
																iconUrl={dependency.iconUrl}
															/>
														)}
													</Tooltip>
													<Box xcss={unlinkButtonStyles}>
														<UnlinkDependencyButton
															ref={unlinkButtonRefs[index]}
															dependency={dependency}
															onUnlinkDependency={onUnlinkDependency}
															focusNextUnlinkButton={() => focusNextUnlinkButton(index)}
														/>
													</Box>
												</Fragment>
											))}
										</Grid>
									</Box>
								) : null}
								<Box>
									<Label htmlFor="">
										{formatMessage(
											expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
												? messages.linkIssueLabelIssueTermRefresh
												: messages.linkIssueLabel,
										)}
									</Label>
									<Flex gap="space.150" alignItems="center" xcss={linkIssueContainerStyles}>
										{/*
											Suppress onKeyDown around the ADS Select components to prevent the modal from closing on escape
											when the select dropdown is open. Fix is currently tracked here:
											https://product-fabric.atlassian.net/browse/DSP-20166
										*/}
										<Box xcss={[noWrapStyles, directionSelectStyles]} onKeyDown={onStopPropagation}>
											<Select
												ref={linkTypeSelect}
												value={linkType}
												isClearable
												placeholder={formatMessage(messages.linkTypePlaceholder)}
												menuPosition="fixed"
												options={linkTypeOptions}
												onChange={handleLinkTypeChange}
												label={
													fg('jsw_roadmaps_timeline-post-project-a11y-fix')
														? formatMessage(messages.linkTypePlaceholder)
														: formatMessage(messages.selectLabel)
												}
											/>
										</Box>
										<Box xcss={[noWrapStyles, flexGrow]} onKeyDown={onStopPropagation}>
											<Select
												value={linkIssue}
												isClearable
												placeholder={formatMessage(
													expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
														? messages.selectIssuePlaceholderIssueTermRefresh
														: messages.selectIssuePlaceholder,
												)}
												menuPosition="fixed"
												options={addDependencyOptions}
												formatOptionLabel={getLinkIssueLabel}
												getOptionValue={(option) => option.issueKey}
												getOptionLabel={(option) =>
													formatMessage(
														expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
															? messages.issueInfoLabelIssueTermRefresh
															: messages.issueInfoLabel,
														{
															issueType: option.issueType,
															issueKey: option.issueKey,
															issueSummary: option.issueSummary,
														},
													)
												}
												onChange={handleLinkIssueChange}
												label={
													fg('jsw_roadmaps_timeline-post-project-a11y-fix')
														? formatMessage(messages.selectIssuePlaceholder)
														: formatMessage(messages.selectLabel)
												}
											/>
										</Box>
										<Button
											appearance="primary"
											isLoading={isAddButtonLoading}
											isDisabled={!linkType || !linkIssue}
											onClick={handleAddDependency}
											aria-label={formatMessage(messages.addAriaLabel)}
											interactionName="timeline-edit-dependency-add-clicked"
										>
											{formatMessage(messages.add)}
										</Button>
									</Flex>
								</Box>
							</>
						) : (
							<Box xcss={spinnerStyles}>
								<Spinner
									interactionName="load"
									size="large"
									label={formatMessage(
										expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
											? messages.isLoadingLabelIssueTermRefresh
											: messages.isLoadingLabel,
									)}
								/>
							</Box>
						)}
					</Stack>
				</ModalBody>
				<ModalFooter>
					<Button
						appearance="subtle"
						onClick={handleOnClose}
						interactionName="timeline-edit-dependency-ok-clicked"
					>
						{formatMessage(messages.close)}
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

const warningStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
});

const noWrapStyles = xcss({
	whiteSpace: 'nowrap',
});

const linkedIssuesContainer = xcss({
	overflow: 'auto',
	maxHeight: '194px',
	paddingBottom: 'space.050',
});

const unlinkButtonStyles = xcss({
	marginRight: 'space.050',
	marginTop: 'space.050',
	marginBottom: 'space.050',
});

const directionSelectStyles = xcss({
	minWidth: '160px',
});

const flexGrow = xcss({
	flex: '1',
});

const linkIssueContainerStyles = xcss({ marginBottom: 'space.025' });

const spinnerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: 'space.300',
});

EditDependencyModal.defaultProps = {
	errors: [],
	isLoading: false,
	shouldReturnFocus: true,
};

export default EditDependencyModal;
